import React from "react";
import styled from "styled-components";
import { formats } from "common/constants";
import { expressAsDataSpeed } from "common/utils";

const isLightColor = (color) => {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 0 + 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
};

const getColor = (color) => {
  if (color === "transparent") return "#555";

  return isLightColor(color) ? "black" : "white";
};

const ColorLabel = styled.div`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`;

export function ColorLabelCell({ value, color, onClick }) {
  if (value == undefined) {
    return null;
  }
  if (value === null) {
    return "n/a";
  }
  return (
    <ColorLabel
      className="color-label"
      color={getColor(color)}
      backgroundColor={color}
    >
      {value}
    </ColorLabel>
  );
}

export function ColorLabelNumberCell({ value, color, precision }) {
  if (value == undefined) {
    return null;
  }
  if (value === null) {
    return "n/a";
  }
  return (
    <ColorLabel
      className="color-label-number"
      color={getColor(color)}
      backgroundColor={color}
    >
      {precision ? formats[precision](parseFloat(value)) : parseFloat(value)}
    </ColorLabel>
  );
}

const formatPrecision = (value, precision, thousands = true, roundingMode) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: thousands,
    ...(roundingMode !== undefined ? { roundingMode } : {}),
  });

export const dynamicPrecision = (value, precisions = [], thousands, roundingMode) => {
  const [_threshold, precision] = precisions.find(
    ([below, precision]) => value < below
  ) || [0, 0];
  return formatPrecision(value, precision, thousands, roundingMode);
};

export function NumberCell({
  value,
  precision,
  hasNA,
  percent,
  thousands = true,
}) {
  if (hasNA) {
    if (value === "n/a" || value === undefined || value === null) {
      return "n/a";
    }
  }
  if (precision !== undefined) {
    if (Array.isArray(precision)) {
      return dynamicPrecision(value, precision, thousands);
    }
    if (typeof precision === "number") {
      if (percent) {
        return `${parseFloat(value).toFixed(precision)}%`;
      }
      return formatPrecision(parseFloat(value), precision, thousands);
    }
    return formats[precision](parseFloat(value));
  }
  if (percent) {
    return `${parseFloat(value)}%`;
  }
  return parseFloat(value);
}

export function TimeCell({ value }) {
  if (value === null) {
    return "n/a";
  }
  return value;
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  i {
    font-size: 18px;
  }
`;

export function TextWithIcon({ value, icon, className, onClick }) {
  if (icon) {
    return (
      <IconWrapper className={className} onClick={onClick}>
        <i className="material-icons">{icon}</i>
        {value}
      </IconWrapper>
    );
  }
  if (className || onClick) {
    return (
      <div className={className} onClick={onClick}>
        {value}
      </div>
    );
  }

  return value;
}

export const renderShapeCell = (value) => {
  if (value === null || value === "no" || value === false || value === "n/a") {
    return "no";
  }
  if (String(value).endsWith("%")) {
    return value;
  }
  return expressAsDataSpeed(parseFloat(value));
};

export const TickOrCross = ({ ok = false }) => (
  <i className={`material-icons ${ok ? "col-green" : "col-red"}`}>
    {ok ? "check" : "close"}
  </i>
);
